import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { UserCog, Loader2, CheckCircle2, AlertCircle, Lock, CalendarClock, Plus, X, Trophy, TrendingUp, ImagePlus, Trash2, Pencil, ListOrdered, Star, Video, ShieldCheck, FileText, Award } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const GENDERS = ["", "male", "female", "prefer_not_to_say"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function WorkerProfile() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [data, setData] = useState<any>(null);
  const [f, setF] = useState<any>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (!user) return;
    api.workerProfile().then((d) => {
      setData(d);
      setF({
        headline: d.profile.headline || "", bio: d.profile.bio || "", city: d.profile.city || "", province: d.profile.province || "",
        primaryRole: d.profile.primaryRole || "", skills: (d.profile.skills || []).join(", "),
        yearsExperience: d.profile.yearsExperience ?? "", expectedRate: d.profile.expectedRate ?? "",
        birthdate: d.profile.birthdate || "", gender: d.profile.gender || "", nationality: d.profile.nationality || "",
        videoIntroUrl: d.profile.videoIntroUrl || "",
      });
    }).catch(() => setData(null));
  }, [user]);

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;
  const isWorker = (user.capabilities || []).some((c) => c === "find_work" || c === "offer_service");
  if (!isWorker) return <div className="container-x py-10 max-w-2xl"><div className="card p-5 text-sm text-navy-700">Enable a worker activity (Find Work / Offer a Service) to set up a worker profile.</div></div>;
  if (!data) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const locked = data.personalLock?.locked;
  const lockUntil = data.personalLock?.until ? String(data.personalLock.until).slice(0, 10) : null;
  const min = data.minDailyRate || 695;

  function payload() {
    return {
      headline: f.headline.trim(), bio: f.bio.trim(), city: f.city.trim(), province: f.province.trim(),
      primaryRole: f.primaryRole.trim(),
      skills: f.skills.split(",").map((s: string) => s.trim()).filter(Boolean),
      yearsExperience: f.yearsExperience === "" ? undefined : Number(f.yearsExperience),
      expectedRate: f.expectedRate === "" ? undefined : Number(f.expectedRate),
      birthdate: f.birthdate || undefined, gender: f.gender || undefined, nationality: f.nationality.trim() || undefined,
      videoIntroUrl: (f.videoIntroUrl || "").trim(),
    };
  }
  async function save(onboard: boolean) {
    setErr(null); setOkMsg(null);
    // Mobile #13: enforce the minimum daily rate before saving.
    if (f.expectedRate !== "" && Number(f.expectedRate) < min) {
      setErr(`Expected daily rate must be at least ₱${min}/day.`);
      return;
    }
    setBusy(true);
    try {
      const d = onboard ? await api.completeWorkerOnboarding(payload()) : await api.updateWorkerProfile(payload());
      setData(d);
      setOkMsg(onboard ? "Onboarding complete — your profile is set up." : "Profile saved.");
    } catch (e: any) { setErr(e?.message || "Could not save."); }
    finally { setBusy(false); }
  }

  return (
    <div className="container-x py-6 max-w-2xl">
      <h1 className="text-2xl font-extrabold text-navy mb-1 flex items-center gap-2"><UserCog className="w-6 h-6 text-brand-red" />Worker profile</h1>
      <p className="text-mut text-sm mb-4">A complete profile ranks higher and wins more work.</p>

      {/* Completeness meter */}
      <div className="card p-4 mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold text-navy">Profile completeness</span>
          <span className="font-bold text-navy">{data.completeness}%</span>
        </div>
        <div className="h-2 rounded-full bg-navy-50 overflow-hidden">
          <div className="h-full bg-brand-green transition-all" style={{ width: `${data.completeness}%` }} />
        </div>
        {!data.onboarded && <p className="text-xs text-mut mt-2">Finish the required fields and submit to complete onboarding.</p>}
      </div>

      <StarCard />

      {/* Professional */}
      <div className="card p-5 mb-4 space-y-3">
        <h3 className="font-bold text-navy">Professional</h3>
        <div><label className="label">Headline *</label><input className="field" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} placeholder="e.g. Licensed Electrician" /></div>
        <div><label className="label">About you</label><textarea className="field min-h-[90px]" maxLength={1000} value={f.bio || ""} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="Your experience, specialties, notable projects…" /><p className="text-[11px] text-mut text-right mt-0.5">{(f.bio || "").length}/1000 characters</p></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="label">City *</label><input className="field" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="e.g. Cebu City" /></div>
          <div><label className="label">Province</label><input className="field" value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} placeholder="e.g. Cebu" /></div>
          <div><label className="label">Primary role *</label><input className="field" value={f.primaryRole} onChange={(e) => setF({ ...f, primaryRole: e.target.value })} placeholder="e.g. Electrician" /></div>
          <div><label className="label">Years of experience *</label><input className="field" type="number" min="0" max="70" value={f.yearsExperience} onChange={(e) => setF({ ...f, yearsExperience: e.target.value })} placeholder="e.g. 10" /></div>
        </div>
        <div><label className="label">Skills (comma-separated)</label><input className="field" value={f.skills} onChange={(e) => setF({ ...f, skills: e.target.value })} placeholder="Wiring, Panel installation, Troubleshooting" /></div>
        <div><label className="label flex items-center gap-1.5"><Video className="w-3.5 h-3.5" />Video introduction (link)</label><input className="field" value={f.videoIntroUrl} onChange={(e) => setF({ ...f, videoIntroUrl: e.target.value })} placeholder="https://youtube.com/… or a video link" /><p className="text-xs text-mut mt-1">A short intro video helps you stand out and is required to become a Star.</p></div>
        <div><label className="label">Expected daily rate (₱) *</label><input className="field" type="number" min={min} value={f.expectedRate} onChange={(e) => setF({ ...f, expectedRate: e.target.value })} placeholder={`At least ₱${min}`} /><p className="text-xs text-mut mt-1">Minimum ₱{min}/day.</p></div>
      </div>

      {/* Personal (lockable) */}
      <div className="card p-5 mb-4 space-y-3">
        <h3 className="font-bold text-navy flex items-center gap-2">Personal {locked && <span className="chip bg-navy-50 text-navy-600 text-xs"><Lock className="w-3 h-3" />Locked until {lockUntil}</span>}</h3>
        {locked && <p className="text-xs text-mut">To protect trust, personal details are locked for 30 days after onboarding. Contact support to change them sooner.</p>}
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="label">Birthdate</label><input className="field" type="date" disabled={locked} value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} /></div>
          <div><label className="label">Gender</label><select className="field" disabled={locked} value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}>{GENDERS.map((g) => <option key={g} value={g}>{g ? g.replace(/_/g, " ") : "—"}</option>)}</select></div>
          <div><label className="label">Nationality</label><input className="field" disabled={locked} value={f.nationality} onChange={(e) => setF({ ...f, nationality: e.target.value })} placeholder="e.g. Filipino" /></div>
        </div>
      </div>

      {err && <p className="text-sm text-brand-red flex items-center gap-1 mb-2"><AlertCircle className="w-4 h-4" />{err}</p>}
      {okMsg && <p className="text-sm text-brand-green flex items-center gap-1 mb-2"><CheckCircle2 className="w-4 h-4" />{okMsg}</p>}

      <div className="flex gap-2">
        {data.onboarded ? (
          <button onClick={() => save(false)} disabled={busy} className="btn-red disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Save profile</button>
        ) : (
          <button onClick={() => save(true)} disabled={busy} className="btn-red disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}Complete onboarding</button>
        )}
      </div>

      <VerificationQueuePanel workerVerified={!!user.workerVerified} />
      <PerformancePanel />
      <PortfolioEditor />
      <AvailabilityEditor />
    </div>
  );
}

function ScoreTrend() {
  const [hist, setHist] = useState<{ finalScore: number; computedAt: string }[] | null>(null);
  useEffect(() => { api.myScoreHistory().then((h) => setHist(h)).catch(() => setHist([])); }, []);
  if (!hist || hist.length < 2) {
    return <p className="text-xs text-mut mt-3 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />Your score trend will appear here as it changes over time.</p>;
  }
  const vals = hist.map((h) => h.finalScore);
  const max = 100, min = 0;
  const W = 260, H = 48;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / (max - min)) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const first = vals[0], last = vals[vals.length - 1];
  const delta = last - first;
  return (
    <div className="mt-4 pt-3 border-t border-line">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-navy flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" />Score trend</span>
        <span className={`text-xs font-semibold ${delta > 0 ? "text-brand-green" : delta < 0 ? "text-brand-red" : "text-mut"}`}>{delta > 0 ? `+${delta}` : delta} over {hist.length} points</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12" preserveAspectRatio="none">
        <polyline points={pts} fill="none" stroke="#1e3a5f" strokeWidth="2" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

function PerformancePanel() {
  const [s, setS] = useState<any>(null);
  useEffect(() => { api.myWorkerScore().then(setS).catch(() => setS(null)); }, []);
  if (!s) return null;
  const tierColor: Record<string, string> = { A: "bg-emerald-100 text-emerald-700", B: "bg-amber-100 text-amber-700", C: "bg-navy-50 text-navy-600" };
  const b = s.breakdown || {};
  const rows: [string, number, number][] = [
    ["Reviews", b.reviews || 0, 40], ["Completed work", b.deals || 0, 20],
    ["Credentials", b.credentials || 0, 15], ["Profile", b.profile || 0, 15], ["Verified", b.verified || 0, 10],
  ];
  return (
    <div className="card p-5 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-navy flex items-center gap-2"><Trophy className="w-4 h-4" />Performance</h3>
        <div className="flex items-center gap-2">
          <span className={`chip ${tierColor[s.tier]}`}>Tier {s.tier}</span>
          <span className="text-xl font-extrabold text-navy">{s.finalScore}<span className="text-sm text-mut font-normal">/100</span></span>
        </div>
      </div>
      <div className="space-y-2">
        {rows.map(([label, val, max]) => (
          <div key={label}>
            <div className="flex justify-between text-xs text-mut mb-0.5"><span>{label}</span><span>{val}/{max}</span></div>
            <div className="h-1.5 rounded-full bg-navy-50 overflow-hidden"><div className="h-full bg-navy" style={{ width: `${max ? (val / max) * 100 : 0}%` }} /></div>
          </div>
        ))}
      </div>
      <p className="text-xs text-mut mt-3">Higher scores rank you above other workers in search. Earn reviews, complete work, add verified credentials, and finish your profile to climb tiers.</p>
      <ScoreTrend />
    </div>
  );
}

function VerificationQueuePanel({ workerVerified }: { workerVerified: boolean }) {
  const [q, setQ] = useState<any>(null);
  useEffect(() => { api.myVerificationQueue().then(setQ).catch(() => setQ(null)); }, []);
  if (!q) return null;
  if (workerVerified && !q.inQueue) return null; // already verified, nothing pending
  if (!q.inQueue) return null; // not in the queue (no pending submission)
  return (
    <div className="card p-5 mt-4 border-l-4 border-brand-gold">
      <h3 className="font-bold text-navy mb-1 flex items-center gap-2"><ListOrdered className="w-4 h-4" />Verification in progress</h3>
      <p className="text-sm text-mut">Your KYC documents are in the review queue.</p>
      <div className="flex items-end gap-4 mt-3">
        <div>
          <div className="text-3xl font-extrabold text-navy">#{q.position}</div>
          <div className="text-xs text-mut">your position</div>
        </div>
        <div className="text-sm text-mut mb-1">of <span className="font-semibold text-navy">{q.total}</span> pending {q.total === 1 ? "review" : "reviews"}</div>
      </div>
      <div className="h-2 rounded-full bg-navy-50 overflow-hidden mt-3">
        <div className="h-full bg-brand-gold transition-all" style={{ width: `${q.total ? Math.max(6, ((q.total - q.position + 1) / q.total) * 100) : 100}%` }} />
      </div>
      <p className="text-xs text-mut mt-2">We review submissions in the order received. You'll be notified as soon as a decision is made.</p>
    </div>
  );
}

function PortfolioEditor() {
  const [media, setMedia] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ title: "", caption: "" });

  async function load() { try { setMedia(await api.myMedia()); } catch { setMedia([]); } }
  useEffect(() => { load(); }, []);
  if (!media) return null;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; if (!file) return;
    setErr(null); setBusy(true);
    try { await api.uploadMedia(file); await load(); }
    catch (ex: any) { setErr(ex?.message || "Could not upload image."); }
    finally { setBusy(false); e.target.value = ""; }
  }
  async function remove(id: string) {
    setBusy(true);
    try { await api.deleteMedia(id); await load(); } catch { /* ignore */ } finally { setBusy(false); }
  }
  function startEdit(m: any) { setEditing(m.id); setDraft({ title: m.title || "", caption: m.caption || "" }); }
  async function saveEdit(id: string) {
    setBusy(true);
    try { await api.updateMedia(id, { title: draft.title, caption: draft.caption }); setEditing(null); await load(); }
    catch (ex: any) { setErr(ex?.message || "Could not save."); } finally { setBusy(false); }
  }

  return (
    <div className="card p-5 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-navy flex items-center gap-2"><ImagePlus className="w-4 h-4" />Portfolio</h3>
        <label className={`btn-outline h-9 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          <Plus className="w-4 h-4" />Add photo
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} disabled={busy} />
        </label>
      </div>
      <p className="text-xs text-mut mb-3">Showcase photos of your past work. These appear on your public profile. Up to 12 images.</p>
      {err && <p className="text-sm text-brand-red mb-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}

      {media.length === 0 ? (
        <div className="text-sm text-mut text-center py-6 border border-dashed border-line rounded-lg">No portfolio photos yet. Add your best work to stand out.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {media.map((m) => (
            <div key={m.id} className="rounded-lg overflow-hidden border border-line group relative">
              <img src={m.url} alt={m.title || "Portfolio item"} className="w-full h-28 object-cover" />
              {m.status && m.status !== "approved" && (<span className={`absolute top-1 left-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${m.status === "rejected" ? "bg-brand-red text-white" : "bg-brand-gold text-white"}`}>{m.status === "rejected" ? "Rejected" : "In review"}</span>)}
              {editing === m.id ? (
                <div className="p-2 space-y-1.5">
                  <input className="field h-8 text-xs" placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                  <input className="field h-8 text-xs" placeholder="Caption" value={draft.caption} onChange={(e) => setDraft({ ...draft, caption: e.target.value })} />
                  <div className="flex gap-1">
                    <button onClick={() => saveEdit(m.id)} disabled={busy} className="btn-red h-7 text-xs flex-1">Save</button>
                    <button onClick={() => setEditing(null)} className="btn-ghost h-7 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="p-2">
                  {m.title && <div className="text-xs font-semibold text-navy truncate">{m.title}</div>}
                  {m.caption && <div className="text-[11px] text-mut truncate">{m.caption}</div>}
                  <div className="flex gap-1 mt-1">
                    <button onClick={() => startEdit(m)} className="btn-ghost h-7 w-7 p-0 text-navy" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(m.id)} disabled={busy} className="btn-ghost h-7 w-7 p-0 text-brand-red" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AvailabilityEditor() {
  const [av, setAv] = useState<any>(null);
  const [day, setDay] = useState(1);
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("17:00");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() { try { setAv(await api.myAvailability()); } catch { setAv({ status: "available", slots: [] }); } }
  useEffect(() => { load(); }, []);
  if (!av) return null;

  async function setStatus(s: "available" | "busy" | "unavailable") { setAv(await api.setAvailabilityStatus(s)); }
  async function addSlot() {
    setErr(null); setBusy(true);
    try { setAv(await api.addAvailabilitySlot(day, start, end)); }
    catch (e: any) { setErr(e?.message || "Could not add slot."); }
    finally { setBusy(false); }
  }
  async function delSlot(id: string) { setAv(await api.deleteAvailabilitySlot(id)); }

  const STATUS: [string, string][] = [["available", "Available"], ["busy", "Busy"], ["unavailable", "Not available"]];
  const color: Record<string, string> = { available: "bg-emerald-100 text-emerald-700", busy: "bg-amber-100 text-amber-700", unavailable: "bg-navy-50 text-mut" };

  return (
    <div className="card p-5 mt-4">
      <h3 className="font-bold text-navy mb-1 flex items-center gap-2"><CalendarClock className="w-4 h-4" />Availability</h3>
      {/* QA #33: make clear the shown status is a default until the user sets it. */}
      <p className="text-xs text-mut mb-3">Your status defaults to <b>Available</b>. Set your real status and add specific hours below so clients see accurate availability.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS.map(([v, label]) => (
          <button key={v} onClick={() => setStatus(v as any)} className={`chip ${av.status === v ? color[v] + " ring-1 ring-navy/20" : "bg-white border border-line text-mut hover:text-navy"}`}>{label}</button>
        ))}
      </div>

      {av.slots.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {av.slots.map((s: any) => (
            <div key={s.id} className="flex items-center gap-2 text-sm">
              <span className="w-10 font-semibold text-navy">{DAYS[s.dayOfWeek]}</span>
              <span className="text-mut">{s.startTime} – {s.endTime}</span>
              <button onClick={() => delSlot(s.id)} className="btn-ghost h-6 w-6 p-0 text-brand-red ml-1" title="Remove"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div><label className="label">Day</label><select className="field h-9" value={day} onChange={(e) => setDay(Number(e.target.value))}>{DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}</select></div>
        <div><label className="label">From</label><input className="field h-9" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div><label className="label">To</label><input className="field h-9" type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        <button onClick={addSlot} disabled={busy} className="btn-outline h-9 disabled:opacity-50"><Plus className="w-4 h-4" />Add slot</button>
      </div>
      {err && <p className="text-sm text-brand-red mt-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}
    </div>
  );
}


function StarCard() {
  const [st, setSt] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function load() { setSt(await api.starStatus().catch(() => null)); }
  useEffect(() => { load(); }, []);
  async function claim() {
    setBusy(true); setMsg(null);
    try { const r = await api.claimStar(); if (r?.isStar) { setMsg("⭐ You're now a Star! Your profile is featured and boosted in search."); await load(); } }
    catch (e: any) { setMsg(e?.message || "Not eligible yet."); } finally { setBusy(false); }
  }
  const actions = [
    { label: "Get Verified", to: "/get-verified", Icon: ShieldCheck },
    { label: "Add licenses", to: "/get-verified", Icon: Award },
    { label: "Add your résumé", to: "/post/resume", Icon: FileText },
  ];
  return (
    <div className="card p-5 mb-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-bold text-navy flex items-center gap-2"><Star className="w-4 h-4 text-brand-gold" />Become a Star</h3>
        {st?.isStar && <span className="chip bg-brand-gold/15 text-[#8a5a00] font-bold"><Star className="w-3.5 h-3.5" />Star member</span>}
      </div>
      {!st ? <div className="text-sm text-mut">Loading…</div> : st.isStar ? (
        <p className="text-sm text-navy-700">You're a featured Star ⭐ — your profile ranks above non-Star workers in search and carries a Star badge.</p>
      ) : (
        <>
          <p className="text-sm text-mut mb-3">Stars are featured and boosted in search. Complete all of the below to unlock it:</p>
          <div className="space-y-1.5 mb-3">
            {st.requirements.map((r: any) => (
              <div key={r.key} className="flex items-center gap-2 text-sm">
                {r.done ? <CheckCircle2 className="w-4 h-4 text-brand-green shrink-0" /> : <div className="w-4 h-4 rounded-full border-2 border-line shrink-0" />}
                <span className={r.done ? "text-navy" : "text-mut"}>{r.label}</span>
              </div>
            ))}
          </div>
          <button onClick={claim} disabled={busy || !st.eligible} className="btn-red h-10 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}{st.eligible ? "Become a Star" : "Complete requirements to unlock"}
          </button>
          {msg && <p className="text-sm mt-2 text-navy">{msg}</p>}
        </>
      )}
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-line">
        {actions.map((a) => (<Link key={a.label} href={a.to} className="btn-outline h-9 px-3 text-xs"><a.Icon className="w-3.5 h-3.5" />{a.label}</Link>))}
      </div>
    </div>
  );
}
